import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type ThreeDPanelState } from "@/shared/state/visualization";
import type { EntityRegistry } from "@/shared/state/visualization/schema/v1/three-d";

import { type PanelSettingsSidebarProps } from "../../sidebarContext";
import { BasePanelSettings } from "../BasePanelSettings";
import { SettingsSection } from "../SettingsSection";
import styles from "../SettingsSection.module.css";
import { Spotlight } from "../Spotlight";

import { EntityList } from "./EntityConfig/EntityList";
import { CameraSettings } from "./SceneConfig/CameraSettings";
import { GridSettings } from "./SceneConfig/GridSettings";

export interface ThreeDPanelSettingsSidebarProps
  extends PanelSettingsSidebarProps {
  field?: `Entity ${string}` | "Add entity";
}

interface ThreeDPanelSettingsProps extends ThreeDPanelSettingsSidebarProps {
  state: ThreeDPanelState;
}

/**
 * The panel's entity registry, falling back to one derived from its components.
 *
 * A workspace saved before the registry existed has components with no registry
 * beside them; deriving a flat one keeps those panels editable.
 */
function entityRegistryOf(state: ThreeDPanelState): EntityRegistry {
  const declared = state.config?.entities;
  if (declared && Object.keys(declared).length > 0) {
    return declared;
  }
  return Object.fromEntries(
    Object.keys(state.data.entities).map((id) => [id, {}]),
  );
}

export function ThreeDPanelSettings({
  field,
  state,
  spotlightKey,
}: ThreeDPanelSettingsProps) {
  // The one place the entity tree's release gates are read. `MeshConfig` and
  // `EntityConfig` take the answers as props rather than calling this
  // themselves: the flag hook reaches for domain services, and the entity forms
  // are deliberately a plain set of controls any tree can render.
  //
  // Authoring only. A panel whose entity already carries a model or a point
  // cloud still shows it — and still draws it — with the flag off.
  const modelMeshEnabled = useCheckFeatureFlag("release-3d-assets");
  const pointCloudEnabled = useCheckFeatureFlag("release-point-clouds");

  return (
    <BasePanelSettings state={state}>
      <SettingsSection title="Camera">
        <CameraSettings panelId={state.id} />
      </SettingsSection>
      {/* No models here: geometry belongs to an entity's Mesh, so a model is
          chosen there and that one entity draws the whole file. */}
      <SettingsSection title="Scenery">
        <GridSettings panelId={state.id} />
      </SettingsSection>
      {/* One collapsible section holding the whole tree, rather than a section
          per entity: a chain of five links produced five sibling sections with
          no way to collapse any of them, burying everything below. */}
      <SettingsSection title="Entities" collapsible>
        <Spotlight
          className={styles.settingContainer}
          // Both `Entity <id>` and `Add entity` land on the list now. Targeting
          // an individual row would need the tree to force that row open.
          spotlightKey={
            field === "Add entity" || field?.startsWith("Entity ")
              ? spotlightKey
              : undefined
          }
        >
          <EntityList
            registry={entityRegistryOf(state)}
            components={state.data.entities}
            panelId={state.id}
            modelMeshEnabled={modelMeshEnabled}
            pointCloudEnabled={pointCloudEnabled}
          />
        </Spotlight>
      </SettingsSection>
    </BasePanelSettings>
  );
}
